import React, { FC, useEffect, useState, useRef } from 'react';
import { notification } from 'antd';
import Checkbox from 'components/Elements/Checkbox';
import CheckboxDropdown from 'components/Elements/CheckboxDropdown';
import Dropdown from 'components/Elements/Dropdown';
import moment from 'moment';
import Button from 'components/Elements/Button';
import Tooltip from 'components/Elements/Tootltip';
import WarningBox from 'components/Elements/WarningBox';
import TimePicker from 'components/Elements/TimePicker';
import { DropdownOption } from 'components/Elements/Dropdown/types';
import { isEmpty } from 'underscore';
import { calculateBilledHours } from 'core/utils/billedHours';
import { retailCoverDefaultStart, retailGeneralDefaultStartTime } from 'core/utils/retailDateHelpers';
import {
    IsoDateString,
    TimeString,
    UsDateString,
    addHoursToTime,
    parseShiftDateTime,
    parseUsOrIso,
    usToIso,
} from 'core/utils/date';
import { WarningIcon } from '../../Elements/CustomSVGIcon';
import FormSection from '../FormSection';
import './shiftSelector.scss';
import SavedShift from './SavedShiftDisplay';
import { SaveShiftValues } from './types';

interface TimeSlot {
    label: string;
    startTime: TimeString;
    endTime: TimeString;
    /** Weekday (0=Sun..6=Sat) this slot belongs to, for per-day retail shifts */
    day?: number | null;
}

interface ShiftOption {
    /** Clean shift type name for persistence (e.g. "Day Shift") */
    name: string;
    /** Display label shown in dropdown — falls back to name if not set */
    label?: string;
    value: string;
    timeSlots: TimeSlot[];
}

/** Get the display label for a shift option, falling back to name */
const getShiftLabel = (opt: ShiftOption): string => opt.label || opt.name;

/** Shift type value for Open Day — matches SiteShift::TYPE_OPEN_DAY (40) */
const SHIFT_TYPE_OPEN_DAY = '40';

interface VisitDate {
    shiftType?: string | undefined | number;
    blockDate?: string | undefined | null;
    id: number | null;
    /** "MM-DD-YYYY" — form/dropdown date format */
    date: UsDateString;
    saved: boolean;
    shiftName?: string | undefined;
    startTime?: TimeString | undefined;
    endTime?: TimeString | undefined;
    bookingTimeCritical?: boolean;
}

interface ApprovedVisitInput {
    /** "YYYY-MM-DD" — backend payload format */
    date: IsoDateString;
    startTime: TimeString;
    endTime: TimeString;
    shift: string;
}

interface ShiftSelectorProps {
    visitDates: VisitDate[];
    shiftOptions: ShiftOption[];
    handleSaveShift: (values: SaveShiftValues) => void;
    handleRemoveShift: (date: string) => void;
    handleEditShift: (date: string) => void;
    totalHours?: number;
    openDays?: { day: string, startTime: string, endTime: string }[] | undefined;
    onChangetotalHours?: (hours: number) => void;
    enabled24_7: boolean;
    showBilling?: boolean;
    maxShiftHours?: number;
    isGuardingRequest?: boolean;
    isRetail?: boolean;
    /**
     * Retail opening windows keyed by lowercase weekday name. Used to default a
     * retail general visit's start time to when the store opens.
     */
    openDaysByDay?: import('core/utils/retailDateHelpers').OpenDaysByDay | undefined;
    approvedVisits: ApprovedVisitInput[];
    /**
     * ISO YYYY-MM-DD. A bank holiday suspends the site's open window, so hours
     * that are normally free are billed — the API bills the same way.
     */
    bankHolidays?: string[] | undefined;
    /** Whether to show the admin-only "Emergency Request" option in the Custom shift. */
    isAdmin?: boolean;
    /**
     * Amending an existing Emergency Request: lock the shift to Custom and
     * force "Emergency Request" on (pre-selected, can't be changed/unchecked).
     */
    lockToEmergency?: boolean;
    /**
     * Admin-only number of officers per shift (1-10). When a change handler is
     * provided the stepper is shown above the billed-hours total; each shift is
     * billed for this many officers.
     */
    // eslint-disable-next-line react/require-default-props
    numberOfOfficers?: number;
    // eslint-disable-next-line react/require-default-props
    onChangeNumberOfOfficers?: (value: number) => void;
}

const ShiftSelector: FC<ShiftSelectorProps> = ({
    visitDates, shiftOptions, handleSaveShift, handleRemoveShift, handleEditShift, totalHours,
    openDays, onChangetotalHours, enabled24_7, showBilling, maxShiftHours, isGuardingRequest = false,
    isRetail = false, openDaysByDay, approvedVisits, bankHolidays, isAdmin = false, lockToEmergency = false,
    numberOfOfficers = 1, onChangeNumberOfOfficers,
}) => {
    const originalShiftOption = { label: shiftOptions[0] ? getShiftLabel(shiftOptions[0]) : '', value: shiftOptions[0]?.value || '' };
    // Retail guarding starts with no shift chosen. Picking a shift books it
    // straight away there, so leading with whichever shift happens to head the
    // list would book that one for anybody who did not open the dropdown.
    const isRetailGuarding = isRetail && isGuardingRequest;
    const [isSameShift, setIsSameShift] = useState<boolean>(true);
    const [selectedShift, setSelectedShift] = useState<DropdownOption[]>(isRetailGuarding ? [] : [originalShiftOption]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<DropdownOption[]>([]);
    const [timeSlotError, setTimeSlotError] = useState<string>('');
    const [selectedDates, setSelectedDates] = useState<DropdownOption[]>([]);
    const [isTimeCritical, setIsTimeCritical] = useState<boolean>(false);
    const [customShiftStartTime, setCustomShiftStartTime] = useState<string | null>(null);
    const [customShiftEndTime, setCustomShiftEndTime] = useState<string | null>(null);
    const [customTimeSlotStartTime, setCustomTimeSlotStartTime] = useState<string | null>(null);
    const [timeCriticalReason] = useState<string>('');
    const [isCustomShift, setIsCustomShift] = useState<boolean>(false);
    const [calculateTotalHours, setCalculateTotalHours] = useState<number>(totalHours || 0);
    // Retail general/access uses a simplified shift step: the user picks only a
    // START time; the END time is auto-set to the site's open-day closing time
    // (resolved per date in the parent) and never shown in the UI.
    const isRetailGeneral = isRetail && !isGuardingRequest;
    const [retailGeneralStartTime, setRetailGeneralStartTime] = useState<string | null>(null);
    // Set once the user picks their own start time, so the opening-time default
    // below stops overwriting their choice.
    const hasPickedRetailStartRef = useRef(false);
    const hasAutoSavedOpenDayRef = useRef<boolean>(false);
    const isEditingOrRemovingRef = useRef<boolean>(false);
    const isSavingRef = useRef(false);
    // Set while an Edit is being applied. handleEditShift (parent) marks the
    // edited date unsaved, which changes visitDates and re-fires the
    // first-saved-date pre-select effect below — without this guard that
    // effect overrides the shift/time slot handleEditTime just restored,
    // which is why the time slot only pre-selected intermittently.
    const isEditingRef = useRef(false);
    // The list of visitDates dates seen on the previous render, so the
    // auto-apply effect below can distinguish a genuinely new date (added via
    // the calendar) from one the user just removed the shift from.
    const prevDatesRef = useRef<string[]>([]);
    // The shift label the code last selected. react-dropdown-select fires
    // onChange for a value set in code exactly as it does for one the user
    // clicked, and on retail guarding that handler books the shift. Removing a
    // saved shift drops the selection back to the first option, so the date
    // just emptied was booked again (Night Shift leads the retail list, which
    // is why a removed Day Shift returned as a Night Shift). Naming the value
    // the code set is what tells the two apart; a timing flag is not reliable,
    // because the fallback can land in a later commit.
    const programmaticShiftRef = useRef<string | null>(null);

    // Match by value first — that's the unique identifier — and only fall
    // back to label matching when no value is set yet. The previous strict
    // value-AND-label check left foundShift null whenever the stored label
    // drifted from the current shiftOptions label (e.g. old saved label
    // included a time range that the current transform strips), which
    // collapsed timeSlotOptions to undefined and rendered "No data" in the
    // Time Slots dropdown even though a shift was selected.
    const foundShift = shiftOptions.find(
        (shiftOption) => (selectedShift[0]?.value && shiftOption.value === selectedShift[0].value)
            || (!selectedShift[0]?.value && getShiftLabel(shiftOption) === selectedShift[0]?.label),
    );

    // Every predefined retail guarding shift runs on the store's own hours,
    // which differ by weekday, so there is nothing for the user to pick: no
    // Time Slots dropdown is shown and each date takes the window for its own
    // day. Custom is excluded — it is the one shift the user times themselves.
    const isRetailAutoShift = isRetail
        && isGuardingRequest
        && !!selectedShift[0]?.label
        && selectedShift[0].label !== 'Custom';

    // Retail guarding 24/7 is booked like a retail general visit: pick the
    // start time, pick the dates, then Save. Choosing it applies nothing on
    // its own — the other retail shifts have no time to pick, so they still do.
    const isRetail247Pick = isRetail && isGuardingRequest && selectedShift[0]?.label === '24/7';

    /**
     * The store's window for a date's own weekday. Falls back to a shift that
     * applies to every day, and is undefined when the store keeps no hours for
     * that weekday — the parent then reports the date as skipped.
     *
     * @param  dateStr Date in MM-DD-YYYY
     * @param  option  Shift option to read the slots from
     * @return the slot for that weekday
     */
    const retailSlotForDate = (
        dateStr: UsDateString,
        option: ShiftOption | undefined = foundShift,
    ): TimeSlot | undefined => {
        const dayNum = parseUsOrIso(dateStr).day();
        return option?.timeSlots.find((slot) => slot.day === dayNum)
            ?? option?.timeSlots.find((slot) => slot.day === null || slot.day === undefined);
    };

    // A draft date is "bookable now" if its shift start is still future OR
    // — for Open Day — its end is still in the future today, OR there is
    // already an approved visit for that date and shift type whose end
    // hasn't passed yet (so other requesters can join a slot that was
    // approved for a non-open day).
    const isShiftBookableForDate = (
        dateStr: UsDateString,
        shiftValue: string | undefined,
        shiftLabel: string | undefined,
        startTime: TimeString | undefined,
        endTime?: TimeString | undefined,
    ): boolean => {
        if (!dateStr || !startTime) {
            return false;
        }
        const startDateTime = parseShiftDateTime(dateStr, startTime);
        const now = new Date();
        if (startDateTime > now) {
            return true;
        }
        if (shiftValue === SHIFT_TYPE_OPEN_DAY && endTime) {
            const endDateTime = parseShiftDateTime(dateStr, endTime);
            if (endDateTime <= startDateTime) {
                endDateTime.setDate(endDateTime.getDate() + 1);
            }
            return endDateTime > now;
        }
        if (shiftLabel && approvedVisits.length > 0) {
            const ymd: IsoDateString = usToIso(dateStr);
            const hasActiveMatch = approvedVisits.some((av) => {
                if (av.date !== ymd) return false;
                if (av.shift !== shiftLabel) return false;
                const avStart = new Date(`${ymd}T${av.startTime}`);
                const avEnd = new Date(`${ymd}T${av.endTime}`);
                if (avEnd <= avStart) {
                    avEnd.setDate(avEnd.getDate() + 1);
                }
                return avEnd > now;
            });
            if (hasActiveMatch) {
                return true;
            }
        }
        return false;
    };

    useEffect(() => {
        if (isCustomShift || selectedTimeSlot[0]?.label === 'Custom') {
            setIsSameShift(false);
        }
    }, [isCustomShift, selectedTimeSlot, visitDates]);

    // Tracking the first saved date's shift name + times as a dependency
    // so this effect re-runs when visitDates arrives in a later render
    // (e.g. Back/Amend reloads the saved request after shiftOptions has
    // already been set). Without these deps the effect only fired on
    // first-option label changes, leaving the dropdown stuck on Day Shift
    // when the backend actually returned Night Shift.
    const firstSavedDate = visitDates.find((d) => d.saved && d.shiftName);
    const firstSavedShiftName = firstSavedDate?.shiftName;
    const firstSavedStartTime = firstSavedDate?.startTime;
    const firstSavedEndTime = firstSavedDate?.endTime;
    useEffect(() => {
        // Skip the single re-run triggered by applying an Edit (which marks
        // the edited date unsaved). handleEditTime has already set the shift
        // and time slot for the edited date; re-deriving from the first saved
        // date here would clobber that selection.
        if (isEditingRef.current) {
            isEditingRef.current = false;
            return;
        }
        // If there are saved dates, use their shift type as the initial
        // selection instead of defaulting to the first option.
        if (firstSavedDate) {
            const matchingOption = shiftOptions.find(
                (opt) => opt.name === firstSavedDate.shiftName
                    || getShiftLabel(opt) === firstSavedDate.shiftName,
            );
            if (matchingOption) {
                programmaticShiftRef.current = getShiftLabel(matchingOption);
                setSelectedShift([{ label: getShiftLabel(matchingOption), value: matchingOption.value }]);
                // Also pre-select the matching time slot
                const matchingTimeSlot = matchingOption.timeSlots.find(
                    (ts) => ts.startTime === firstSavedDate.startTime && ts.endTime === firstSavedDate.endTime,
                ) || matchingOption.timeSlots[0];
                if (matchingTimeSlot) {
                    setSelectedTimeSlot([{
                        label: matchingTimeSlot.label,
                        value: matchingTimeSlot.label,
                        startTime: matchingTimeSlot.startTime,
                        endTime: matchingTimeSlot.endTime,
                    }]);
                }
                return;
            }
        }
        // Nothing saved to follow: retail guarding waits for the user to choose
        // rather than falling back to the first option, which would book it.
        if (isRetailGuarding) {
            programmaticShiftRef.current = null;
            setSelectedShift([]);
            return;
        }
        programmaticShiftRef.current = originalShiftOption.label;
        setSelectedShift([originalShiftOption]);
    }, [originalShiftOption.label, firstSavedShiftName, firstSavedStartTime, firstSavedEndTime]);

    // Auto-save Open Day shift when only 1 unsaved date and Open Day is first/selected option
    useEffect(() => {
        // Skip if already auto-saved or if user is editing/removing
        if (hasAutoSavedOpenDayRef.current || isEditingOrRemovingRef.current) {
            isEditingOrRemovingRef.current = false;
            return;
        }

        const unsavedDates = visitDates.filter((date) => !date.saved);
        if (unsavedDates.length !== 1) {
            return;
        }

        const isOpenDayShift = selectedShift[0]?.value === SHIFT_TYPE_OPEN_DAY;
        if (!isOpenDayShift) {
            return;
        }

        const autoFoundShift = shiftOptions.find((shiftOption) => getShiftLabel(shiftOption) === selectedShift[0]?.label);
        const [timeSlot] = autoFoundShift?.timeSlots || [];

        if (!timeSlot) {
            return;
        }

        const todayDate = new Date();
        const dates = unsavedDates.filter((date) => {
            const [month, day, year] = date.date.split('-');
            const slotDate = `${year}-${month}-${day}`;
            const dateTime = new Date(`${slotDate}T${timeSlot.startTime}`);
            const isTimeInThePast = dateTime > todayDate;
            return isTimeInThePast;
        }).map((date) => ({ value: date.date, label: date.date }));

        if (dates.length === 0) {
            notification.error({
                message: 'Invalid Shift Time',
                description: 'The shift you selected has already ended for the selected date. Please choose a future date or shift.',
            });
            return;
        }

        const values = {
            shift: autoFoundShift?.name || selectedShift[0]?.label,
            shiftType: selectedShift[0]?.value,
            timeSlot: timeSlot.label,
            startTime: timeSlot.startTime,
            endTime: timeSlot.endTime,
            dates: dates.map((date) => date.value),
            selectedDates: unsavedDates.map((date) => ({ value: date.date, label: date.date })).map((date) => date.value),
            isTimeCritical: false,
            timeCriticalReason: '',
            customShiftStartTime: '',
            customShiftEndTime: '',
            customTimeSlotStartTime: '',
        };
        hasAutoSavedOpenDayRef.current = true;
        handleSaveShift(values);
    }, [selectedShift, shiftOptions, visitDates]);

    // Belt-and-braces: when shiftOptions is replaced (e.g. parent
    // recomputes it on date selection / today filtering) but the first
    // option's label happens to stay the same string, the useEffect above
    // doesn't fire. selectedShift keeps a stale {label, value} that no
    // longer matches the new options, so `foundShift` returns undefined
    // and the Time Slots dropdown ends up empty until the user re-picks
    // the shift. Snap back to the first option whenever the current
    // selection has fallen out of sync.
    //
    // Uses a functional setState so it sees the value committed by the
    // matching-effect above when both fire in the same commit phase —
    // otherwise this effect's closure reads the stale `selectedShift`
    // and overwrites the saved shift selection (e.g. Night Shift gets
    // replaced by Day Shift, then the timeSlot Dropdown's
    // componentDidUpdate fires onChange with the night-shift time slot
    // and auto-saves with shiftName="Day Shift" + 22:00–07:00 times).
    useEffect(() => {
        const firstOption = shiftOptions[0];
        if (!firstOption) return;
        setSelectedShift((current) => {
            const isInSync = shiftOptions.some((opt) => (
                opt.value === current[0]?.value
                && getShiftLabel(opt) === current[0]?.label
            ));
            if (isInSync) return current;
            // An empty selection is a valid state on retail guarding, not a
            // stale one to snap back into line.
            if (isRetailGuarding && current.length === 0) return current;
            programmaticShiftRef.current = getShiftLabel(firstOption);
            return [{
                label: getShiftLabel(firstOption),
                value: firstOption.value,
            }];
        });
    }, [shiftOptions]);

    // Auto-apply a shift onto dates that were just added via the calendar
    // (e.g. extending an amended request). Without this the new dates arrive
    // unsaved with no shift and stay "hidden under select shift" until the
    // user re-opens the shift/time pickers or re-ticks "Request the same
    // shift for all selected dates". The shift copied is the one on the
    // latest (chronologically last) already-saved date, so extended dates
    // continue the most recent shift even when the booking uses mixed shifts
    // across dates. Reuses the same handleSaveShift path as the manual
    // pickers, so 24/7 (start==end time slot → two 12h blocks), Day and Night
    // all rebuild correctly.
    useEffect(() => {
        // Track the dates present last render so we can tell a genuinely NEW
        // date (added via the calendar) from one whose shift the user just
        // removed. Update before any early return so it always holds the latest.
        const previousDates = prevDatesRef.current;
        prevDatesRef.current = visitDates.map((d) => d.date);

        if (isSavingRef.current || isEditingRef.current) return;

        const savedDates = visitDates.filter((d) => d.saved && d.shiftName);
        // Only auto-apply to dates that are NEW this render — never to a date
        // the user just removed the shift from (it was already present, so it
        // isn't new). This stops the remove/trash button from re-adding the
        // shift immediately.
        const newUnsavedDates = visitDates.filter((d) => !d.saved && !previousDates.includes(d.date));
        if (savedDates.length === 0 || newUnsavedDates.length === 0) return;

        // Copy the shift from the latest saved date. `date` can be either
        // YYYY-MM-DD (backend load) or MM-DD-YYYY (after a frontend save), so
        // parse both formats before picking the most recent.
        const ref = savedDates.slice().sort((a, b) => (
            moment(b.date, ['YYYY-MM-DD', 'MM-DD-YYYY']).valueOf()
            - moment(a.date, ['YYYY-MM-DD', 'MM-DD-YYYY']).valueOf()
        ))[0];
        if (!ref) return;

        // Reconstruct the shift option + time slot from shiftOptions (the same
        // source the manual pickers use). Skip Custom shifts — their per-date
        // block layout isn't safe to auto-clone.
        const matchedShift = shiftOptions.find(
            (opt) => opt.name === ref.shiftName || getShiftLabel(opt) === ref.shiftName,
        );
        if (!matchedShift || matchedShift.value === '30') return;

        const matchingTimeSlot = matchedShift.timeSlots.find(
            (ts) => ts.startTime === ref.startTime && ts.endTime === ref.endTime,
        ) || matchedShift.timeSlots[0];
        if (!matchingTimeSlot) return;

        const isRetailAutoShiftMatch = isRetail
            && isGuardingRequest
            && getShiftLabel(matchedShift) !== 'Custom';
        const bookableNewDates = newUnsavedDates.filter((d) => {
            // A retail shift runs on each date's own weekday hours, so the
            // window is resolved per date rather than copied from the
            // reference date.
            const slot = isRetailAutoShiftMatch
                ? retailSlotForDate(d.date, matchedShift)
                : matchingTimeSlot;
            // No window for that weekday: pass it through so the save path
            // reports it as unavailable rather than dropping it silently.
            if (!slot) {
                return isRetailAutoShiftMatch;
            }
            return isShiftBookableForDate(
                d.date,
                matchedShift.value,
                getShiftLabel(matchedShift),
                slot.startTime,
                slot.endTime,
            );
        }).map((d) => d.date);

        if (bookableNewDates.length === 0) return;

        isSavingRef.current = true;
        try {
            handleSaveShift({
                shift: matchedShift.name || getShiftLabel(matchedShift),
                shiftType: matchedShift.value,
                timeSlot: matchingTimeSlot.label,
                startTime: matchingTimeSlot.startTime,
                endTime: matchingTimeSlot.endTime,
                dates: bookableNewDates,
                selectedDates: newUnsavedDates.map((d) => d.date),
                isTimeCritical: false,
                timeCriticalReason: '',
                customShiftStartTime: '',
                customShiftEndTime: '',
                customTimeSlotStartTime: '',
                isSameShift: true,
            });
        } finally {
            requestAnimationFrame(() => { isSavingRef.current = false; });
        }
    }, [visitDates, shiftOptions]);

    // Amending an existing Emergency Request: lock the shift to Custom and force
    // "Emergency Request" on. The dropdown is limited to Custom (below) and the
    // checkbox is disabled, so neither can be changed.
    useEffect(() => {
        if (!lockToEmergency) return;
        setIsCustomShift(true);
        setIsTimeCritical(true);
        const customOption = shiftOptions.find((opt) => opt.value === '30');
        if (customOption) {
            programmaticShiftRef.current = getShiftLabel(customOption);
            setSelectedShift([{ label: getShiftLabel(customOption), value: customOption.value }]);
        }
    }, [lockToEmergency, shiftOptions]);

    // Billed-hours rules live in core/utils/billedHours so they can be tested
    // against the API's own calculation; the component only supplies the site
    // and request context.
    const calculateTotalHour = (selectedShifts: VisitDate[]) => calculateBilledHours(selectedShifts, {
        enabled24_7,
        isGuardingRequest,
        isRetailGeneralVisit: isRetailGeneral,
        openDays,
        bankHolidays,
    });

    // A retail general visit starts when the store opens, so default the picker
    // to that date's opening time — the earliest one when several dates are
    // selected, since it has to be valid for all of them.
    //
    // Defaults from the calendar selection, not just the "Select Dates for
    // Chosen Shift" dropdown below. Keying it off the dropdown alone left the
    // time blank until the user opened that dropdown, even though the dates
    // were already picked and the opening time was already knowable. Once the
    // user narrows the dropdown, that selection takes over, because the start
    // has to be valid for the dates the shift is actually being saved against.
    useEffect(() => {
        if (!isRetailGeneral || hasPickedRetailStartRef.current) {
            return;
        }

        const opening = retailGeneralDefaultStartTime(
            selectedDates.map((date) => date.value),
            visitDates.filter((date) => !date.saved).map((date) => date.date),
            openDaysByDay,
        );
        if (opening) {
            setRetailGeneralStartTime(opening);
        }
    }, [isRetailGeneral, selectedDates, visitDates, openDaysByDay]);

    useEffect(() => {
        // Report the single-officer (base) total; the backend multiplies by the
        // officer count when it fans out the shifts. The displayed "Total Hours
        // Billed" below multiplies live for the user.
        const baseHours = calculateTotalHour(visitDates);
        onChangetotalHours?.(baseHours);
        setCalculateTotalHours(baseHours);
    }, [visitDates]);

    const handleIsSameShiftChange = () => {
        setIsSameShift(!isSameShift);
        if (!isSameShift) {
            const dates = visitDates.filter((date) => (
                !date.saved
                && isShiftBookableForDate(
                    date.date,
                    selectedShift[0]?.value,
                    selectedShift[0]?.label,
                    selectedTimeSlot[0]?.startTime,
                    selectedTimeSlot[0]?.endTime,
                )
            )).map((date) => ({ value: date.date, label: date.date }));
            const selectedDate = visitDates.filter((date) => !date.saved).map((date) => ({ value: date.date, label: date.date }));
            if (selectedDate.length > 0 && dates.length === 0) {
                notification.error({
                    message: 'Invalid Shift Time',
                    description: 'The shift you selected has already ended for the selected date. Please choose a future date or shift.',
                });
                setIsTimeCritical(false);
                return;
            }
            setSelectedDates([]);
            const values = {
                shift: foundShift?.name || selectedShift[0]?.label,
                shiftType: selectedShift[0]?.value,
                timeSlot: selectedTimeSlot[0]?.value,
                startTime: selectedTimeSlot[0]?.startTime,
                endTime: selectedTimeSlot[0]?.endTime,
                dates: dates.map((date) => date.value),
                selectedDates: selectedDate.map((date) => date.value),
                isTimeCritical,
                timeCriticalReason,
                customShiftStartTime: customShiftStartTime?.substring(0, 5) || '',
                customShiftEndTime: customShiftEndTime?.substring(0, 5) || '',
                customTimeSlotStartTime: customTimeSlotStartTime?.substring(0, 5) || '',
                isSameShift,
            };
            handleSaveShift(values);
        }
        setIsTimeCritical(false);
    };

    const handleTimeSlotChange = (timeSlot: DropdownOption[]) => {
        setSelectedTimeSlot(timeSlot);
        if (!isEmpty(timeSlot)) {
            setTimeSlotError('');
        }
        // Skip the auto-save when this fires as a side-effect of a
        // programmatic setSelectedTimeSlot (react-dropdown-select calls
        // onChange whenever its `values` prop changes). The closure here
        // would otherwise read a stale selectedShift — e.g. on Back, the
        // matching-effect sets selectedTimeSlot to the saved Night Shift
        // slot in the same commit, the Dropdown's componentDidUpdate
        // fires onChange, and the auto-save below would persist
        // shift="Day Shift" with the night-shift start/end times.
        if (isSavingRef.current) return;
        if (isSameShift && timeSlot[0]?.label !== 'Custom' && !isEmpty(timeSlot)) {
            // In single-shift mode (isSameShift=true) the chosen shift
            // applies to every date in the booking, so re-saving must
            // overwrite already-saved entries — otherwise switching shift
            // type leaves the old shift in place and the user has to
            // delete each row manually. Multi-shift mode keeps the
            // unsaved-only filter so per-date assignments aren't clobbered.
            const targetDates = visitDates;

            if (targetDates.length === 0) {
                return;
            }

            const dates = targetDates.filter((date) => isShiftBookableForDate(
                date.date,
                selectedShift[0]?.value,
                selectedShift[0]?.label,
                timeSlot[0]?.startTime,
                timeSlot[0]?.endTime,
            )).map((date) => ({ value: date.date, label: date.date }));
            const selectedDate = targetDates.map((date) => ({ value: date.date, label: date.date }));

            if (dates.length === 0) {
                notification.error({
                    message: 'Invalid Shift Time',
                    description: 'The shift you selected has already ended for the selected date. Please choose a future date or shift.',
                });
                return;
            }

            setSelectedDates([]);
            const values = {
                shift: foundShift?.name || selectedShift[0]?.label,
                shiftType: selectedShift[0]?.value,
                timeSlot: timeSlot[0]?.value,
                startTime: timeSlot[0]?.startTime,
                endTime: timeSlot[0]?.endTime,
                dates: dates.map((date) => date.value),
                selectedDates: selectedDate.map((date) => date.value),
                isTimeCritical,
                timeCriticalReason,
                customShiftStartTime: customShiftStartTime?.substring(0, 5) || '',
                customShiftEndTime: customShiftEndTime?.substring(0, 5) || '',
                customTimeSlotStartTime: customTimeSlotStartTime?.substring(0, 5) || '',
                isSameShift,
            };
            handleSaveShift(values);
        }
    };

    const handleShiftChange = (shift: DropdownOption[]) => {
        // Only a shift the user actually picked is applied. This also runs for
        // a selection made in code, which is what re-booked a date the moment
        // its shift was removed (see programmaticShiftRef). Matching on the
        // value the code set is reliable whichever commit the fallback lands
        // in; isSavingRef only catches the case where it lands in this one.
        const isProgrammaticChange = isSavingRef.current
            || programmaticShiftRef.current === shift[0]?.label;
        programmaticShiftRef.current = null;

        const customShift = shift[0]?.label === 'Custom';
        setIsCustomShift(customShift);
        // Critical time only applies to the Custom shift — clear it when
        // switching to any other shift so the flag/reason box doesn't linger.
        if (!customShift) {
            setIsTimeCritical(false);
        }

        // Block handleShiftDropdownClose from firing with stale state.
        isSavingRef.current = true;

        setSelectedTimeSlot([]);
        setTimeSlotError('');
        setSelectedShift(shift);
        // A retail 24/7 cover start belongs to the shift it was set on.
        if (isRetail) {
            setCustomTimeSlotStartTime(null);
        }

        // Auto-select time slot and save for Open Day shifts.
        // Build save values directly from the `shift` parameter (not
        // `selectedShift` state which hasn't updated yet).
        // Set isSavingRef so handleShiftDropdownClose (which fires
        // immediately after) doesn't double-save.
        const isOpenDay = shift[0]?.value === SHIFT_TYPE_OPEN_DAY;
        // Retail guarding shows no time slot picker, so picking the shift IS the
        // whole choice — apply it here as Open Day does. Without this the save
        // only ever fired when a time slot was chosen, so the step stayed empty
        // and asked for a shift that had already been selected.
        const isRetailAutoShiftPick = isRetail
            && isGuardingRequest
            && !!shift[0]?.label
            && shift[0].label !== 'Custom'
            && shift[0].label !== '24/7';
        if ((isOpenDay || isRetailAutoShiftPick) && !isProgrammaticChange) {
            const matchedShift = shiftOptions.find(
                (opt) => opt.value === shift[0]?.value
                    && getShiftLabel(opt) === shift[0]?.label,
            );
            const [timeSlot] = matchedShift?.timeSlots || [];
            if (timeSlot) {
                // Nothing to show as "the" slot on retail: each date runs on
                // its own weekday's window.
                if (!isRetailAutoShiftPick) {
                    setSelectedTimeSlot([{
                        label: timeSlot.label,
                        value: timeSlot.label,
                        startTime: timeSlot.startTime,
                        endTime: timeSlot.endTime,
                    }]);
                }

                // In single-shift mode, Open Day applies to every date
                // (including ones saved with a different shift) so the
                // change auto-rewrites them. Multi-shift mode keeps the
                // unsaved-only behaviour.
                const targetDates = isSameShift ? visitDates : visitDates.filter((date) => !date.saved);
                if (targetDates.length > 0) {
                    const futureDates = targetDates.filter((date) => {
                        const slot = isRetailAutoShiftPick
                            ? retailSlotForDate(date.date, matchedShift)
                            : timeSlot;
                        // Dates the store keeps no hours for go through so the
                        // save path reports them as unavailable.
                        if (!slot) {
                            return isRetailAutoShiftPick;
                        }
                        return isShiftBookableForDate(
                            date.date,
                            shift[0]?.value,
                            shift[0]?.label,
                            slot.startTime,
                            slot.endTime,
                        );
                    }).map((date) => ({ value: date.date, label: date.date }));

                    if (futureDates.length > 0) {
                        handleSaveShift({
                            shift: matchedShift?.name || shift[0]?.label,
                            shiftType: shift[0]?.value,
                            // Retail times are resolved per date by the parent
                            // from the store's opening hours.
                            timeSlot: isRetailAutoShiftPick ? undefined : timeSlot.label,
                            startTime: isRetailAutoShiftPick ? undefined : timeSlot.startTime,
                            endTime: isRetailAutoShiftPick ? undefined : timeSlot.endTime,
                            dates: futureDates.map((d) => d.value),
                            selectedDates: targetDates.map((d) => d.date),
                            isTimeCritical: false,
                            timeCriticalReason: '',
                            customShiftStartTime: '',
                            customShiftEndTime: '',
                            customTimeSlotStartTime: '',
                            isSameShift,
                        });
                    }
                }
            }
        }
        // Reset after a tick so handleShiftDropdownClose (which fires
        // synchronously after this) sees the flag and skips.
        requestAnimationFrame(() => { isSavingRef.current = false; });
    };

    const handleSelectedDatesChange = (values: DropdownOption[]) => {
        setSelectedDates(values);
    };

    const handleCustomShiftStartTimeChange = (time: string) => {
        setCustomShiftStartTime(time);
        // In Emergency Request mode the admin sets the end time themselves —
        // don't auto-derive it. Otherwise default the end to maxShiftHours
        // (mobile-response cap) or 12h after the start time.
        if (isTimeCritical) {
            return;
        }
        const shiftDuration = maxShiftHours || 12;
        const [hours, minutes] = time.split(':').map(Number);
        const startDate = new Date();
        startDate.setHours(hours || 0, minutes || 0, 0);
        const endDate = new Date(startDate.getTime() + shiftDuration * 60 * 60 * 1000);
        const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}:00`;
        setCustomShiftEndTime(endTime);
    };

    const handleCustomShiftEndTimeChange = (time: string) => {
        setCustomShiftEndTime(time);
    };

    const handleCustomTimeSlotStartTimeChange = (time: string) => {
        setCustomTimeSlotStartTime(time);
    };

    // Admin-only "Emergency Request" toggle inside a Custom shift. When ticked
    // it enables the Shift End time and lifts the 12h cap; the shift is saved
    // through the normal flow and flagged critical.
    const handleIsTimeCriticalChange = () => {
        const leavingEmergency = isTimeCritical;
        setIsTimeCritical(!isTimeCritical);

        // Off again: the end time the admin set is no longer theirs to choose,
        // so it goes back to the normal 12-hour default. Keeping it would
        // strand an overnight end (22:00 to 07:00) on a shift that derives its
        // own end, which then reads as a 9-hour shift nobody asked for. The
        // start is left alone; only the end was locked to Emergency.
        if (leavingEmergency && customShiftStartTime) {
            setCustomShiftEndTime(`${addHoursToTime(customShiftStartTime, maxShiftHours || 12)}:00`);
        }
    };

    // Initialize custom shift times when custom shift is selected
    useEffect(() => {
        if (isCustomShift && !customShiftStartTime) {
            // Initialize with current time rounded to next 15-minute step
            const now = new Date();
            const currentHours = now.getHours();
            const currentMinutes = now.getMinutes();
            const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

            let finalHours = currentHours;
            let finalMinutes = roundedMinutes;

            if (finalMinutes >= 60) {
                finalMinutes = 0;
                finalHours = (finalHours + 1) % 24;
            }

            const startTime = `${String(finalHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}:00`;
            handleCustomShiftStartTimeChange(startTime);
        }
    }, [isCustomShift, customShiftStartTime]);

    const handleSaveShiftClick = () => {
        // Retail general/access: the user only picks a start time. The end time
        // is filled in by the parent from the site's open-day closing time, so
        // there is no time slot to validate here.
        if (isRetailGeneral) {
            if (!retailGeneralStartTime) {
                setTimeSlotError('Please select a start time before saving.');
                return;
            }
            setTimeSlotError('');

            const rgDates = selectedDates.map((date) => ({ value: date.value, label: date.label }));
            if (rgDates.length === 0) return;

            const foundRetailDates = visitDates.filter(
                (date) => rgDates.map((d) => d.value).includes(date.date),
            );
            handleSaveShift({
                id: foundRetailDates[0] ? foundRetailDates[0].id : null,
                shift: foundShift?.name || selectedShift[0]?.label,
                shiftType: selectedShift[0]?.value,
                timeSlot: undefined,
                // End is resolved from the open-day closing time in the parent.
                startTime: retailGeneralStartTime,
                endTime: undefined,
                dates: rgDates.map((d) => d.value),
                selectedDates: selectedDates.map((d) => d.value),
                isTimeCritical: false,
                timeCriticalReason,
                customShiftStartTime: '',
                customShiftEndTime: '',
                customTimeSlotStartTime: '',
                isSameShift,
            });
            setSelectedDates([]);
            // Next batch of dates defaults to their own opening time again.
            hasPickedRetailStartRef.current = false;
            return;
        }

        // A time slot must be chosen whenever the Time Slots picker is shown
        // (i.e. not a Custom shift and not Open Day). Check the slot has a
        // label rather than just a non-empty array: editing can pre-populate
        // an empty-valued slot ({label:'', startTime:''}) when the saved slot
        // no longer matches an option, and changing the shift clears it
        // entirely — both would otherwise save undefined times and render as
        // "Invalid date". The "Custom" 24/7 slot has a label, so it passes.
        // Retail guarding has no picker, so there is nothing to require.
        const timeSlotRequired = !isCustomShift
            && !isRetailAutoShift
            && selectedShift[0]?.value !== SHIFT_TYPE_OPEN_DAY;
        if (timeSlotRequired && !selectedTimeSlot[0]?.label) {
            setTimeSlotError('Please select a time slot before saving.');
            return;
        }
        setTimeSlotError('');

        const dates = selectedDates.filter((date) => {
            if (isRetailAutoShift) {
                const retailSlot = retailSlotForDate(date.value);
                // No window for this weekday means the store cannot be covered
                // then; pass it through so the parent reports it alongside the
                // other skipped dates rather than dropping it silently.
                if (!retailSlot) {
                    return true;
                }
                return isShiftBookableForDate(
                    date.value,
                    selectedShift[0]?.value,
                    selectedShift[0]?.label,
                    retailSlot.startTime,
                    retailSlot.endTime,
                );
            }
            const time = selectedTimeSlot[0]?.startTime || (selectedShift[0]?.value === '30' ? customShiftStartTime?.substring(0, 5) || '' : customTimeSlotStartTime?.substring(0, 5) || '');
            return isShiftBookableForDate(
                date.value,
                selectedShift[0]?.value,
                selectedShift[0]?.label,
                time,
                selectedTimeSlot[0]?.endTime,
            );
        }).map((date) => ({ value: date.value, label: date.label }));

        if (selectedDates.length > 0 && dates.length === 0) {
            notification.error({
                message: 'Invalid Shift Time',
                description: 'The shift you selected has already ended for the selected date. Please choose a future date or shift.',
            });
            return;
        }

        const foundDates = visitDates.filter((date) => dates.map((selectedDate) => selectedDate.value).includes(date.date));
        const values = {
            id: foundDates[0] ? foundDates[0].id : null,
            shift: foundShift?.name || selectedShift[0]?.label,
            shiftType: selectedShift[0]?.value,
            timeSlot: selectedTimeSlot[0]?.value,
            startTime: selectedTimeSlot[0]?.startTime,
            endTime: selectedTimeSlot[0]?.endTime,
            dates: dates.map((date) => date.value),
            selectedDates: selectedDates.map((date) => date.value),
            isTimeCritical,
            timeCriticalReason,
            customShiftStartTime: customShiftStartTime?.substring(0, 5) || '',
            customShiftEndTime: customShiftEndTime?.substring(0, 5) || '',
            customTimeSlotStartTime: customTimeSlotStartTime?.substring(0, 5) || '',
            isSameShift,
        };
        handleSaveShift(values);
        setSelectedDates([]);
    };

    const handleShiftDropdownClose = () => {
        if (isSavingRef.current) return;
        const isOpenDay = selectedShift[0]?.value === SHIFT_TYPE_OPEN_DAY;
        const isCustom = selectedShift[0]?.label === 'Custom';
        const hasSavedDates = visitDates.some((d) => d.saved);

        // Auto-apply when either:
        //  - Open Day (existing behaviour — no time slot picker shown), OR
        //  - retail guarding, which likewise has no time slot picker: re-picking
        //    the shift is the only way back after a shift is removed, so it
        //    must apply whether or not anything is saved yet, OR
        //  - single-shift mode with existing saved dates and a non-custom
        //    shift. Otherwise changing the dropdown without re-picking a
        //    time slot leaves visitDates on the old shift; the user can
        //    hit Next and silently submit the previous shift, which is
        //    exactly the "back/amend shows Day Shift" bug.
        if (isRetail247Pick || (!isOpenDay && !isRetailAutoShift && !(isSameShift && hasSavedDates && !isCustom))) {
            return;
        }

        // Same single-shift vs multi-shift rule as handleTimeSlotChange:
        // overwrite saved entries when isSameShift=true, otherwise leave
        // them alone.
        const targetDates = isSameShift ? visitDates : visitDates.filter((date) => !date.saved);
        if (targetDates.length === 0) return;

        const matchedShift = shiftOptions.find(
            (opt) => opt.value === selectedShift[0]?.value
                && getShiftLabel(opt) === selectedShift[0]?.label,
        );
        const [timeSlot] = matchedShift?.timeSlots || [];
        if (!timeSlot) return;

        isSavingRef.current = true;
        try {
            // Retail guarding shows no slot, so there is none to pre-select.
            if (!isRetailAutoShift) {
                setSelectedTimeSlot([{
                    label: timeSlot.label,
                    value: timeSlot.label,
                    startTime: timeSlot.startTime,
                    endTime: timeSlot.endTime,
                }]);
            }

            const futureDates = targetDates.filter((date) => {
                // Retail windows differ by weekday, so each date is checked
                // against its own rather than the first slot's.
                const slot = isRetailAutoShift ? retailSlotForDate(date.date) : timeSlot;
                // Dates the store keeps no hours for go through so the save
                // path reports them as unavailable.
                if (!slot) {
                    return isRetailAutoShift;
                }
                return isShiftBookableForDate(
                    date.date,
                    selectedShift[0]?.value,
                    selectedShift[0]?.label,
                    slot.startTime,
                    slot.endTime,
                );
            }).map((date) => ({ value: date.date, label: date.date }));

            if (futureDates.length > 0) {
                handleSaveShift({
                    shift: matchedShift?.name || selectedShift[0]?.label,
                    shiftType: selectedShift[0]?.value,
                    // Resolved per date by the parent on retail.
                    timeSlot: isRetailAutoShift ? undefined : timeSlot.label,
                    startTime: isRetailAutoShift ? undefined : timeSlot.startTime,
                    endTime: isRetailAutoShift ? undefined : timeSlot.endTime,
                    dates: futureDates.map((d) => d.value),
                    selectedDates: targetDates.map((d) => d.date),
                    isTimeCritical: false,
                    timeCriticalReason: '',
                    customShiftStartTime: '',
                    customShiftEndTime: '',
                    // A retail 24/7 keeps the cover start already picked.
                    customTimeSlotStartTime: isRetailAutoShift ? customTimeSlotStartTime?.substring(0, 5) || '' : '',
                    isSameShift,
                });
            }
        } finally {
            isSavingRef.current = false;
        }
    };

    /**
     * Retail 24/7: the time the cover starts on its first day. Re-applies the
     * shift to the dates it covers so the first day's blocks move with it;
     * clearing it puts the first day back on the store's own opening.
     *
     * @param  time HH:mm:ss, or null to clear
     */
    const handleRetail247StartChange = (time: string | null) => {
        setCustomTimeSlotStartTime(time);
    };

    const handleDatesDropdownClose = () => {
        if (isSavingRef.current) return;
        if (selectedDates.length > 0 && selectedShift.length > 0 && (selectedTimeSlot.length > 0 || selectedShift[0]?.value === SHIFT_TYPE_OPEN_DAY)) {
            isSavingRef.current = true;
            try {
                handleSaveShiftClick();
            } finally {
                isSavingRef.current = false;
            }
        }
    };

    const handleRemoveTime = (date: string) => {
        // Guard the synchronous auto-save paths (time-slot change /
        // dropdown close) and the Open Day auto-save effect while the
        // removal re-renders settle.
        isSavingRef.current = true;
        isEditingOrRemovingRef.current = true;
        setSelectedDates([]);
        // Do NOT force isSameShift back on here. In multi-shift mode (per-date
        // shifts with different times) removing the FIRST saved shift changes
        // the "first saved shift", so the matching effect re-syncs
        // selectedTimeSlot and the Dropdown's onChange fires
        // handleTimeSlotChange. With isSameShift forced true, that handler
        // re-saves the shift across every date — re-adding one to the date we
        // just removed, so the first shift appeared impossible to delete until
        // it was the last one left. Leaving the user's chosen mode intact
        // keeps the auto-save branch disabled in per-date mode.
        handleRemoveShift(date);
        // Reset after a tick, once the re-render cascade has settled.
        requestAnimationFrame(() => { isSavingRef.current = false; });
    };

    const handleEditTime = (date: string) => {
        // Tell the first-saved-date pre-select effect to skip the re-run that
        // handleEditShift's visitDates change will trigger, so it doesn't
        // override the shift/time slot we restore below.
        isEditingRef.current = true;

        setSelectedDates([]);
        const foundDate = visitDates.find((visitDate) => visitDate.date === date);
        const editShift = shiftOptions.find((shiftOption) => shiftOption.name === foundDate?.shiftName);
        const foundTimeSlot = shiftOptions
            .find((shiftOption) => shiftOption.name === foundDate?.shiftName)
            ?.timeSlots.find((timeSlot) => {
                // Compare on HH:mm only — the slot stores HH:mm:ss but the
                // saved date may store either, so normalise both sides.
                const timeSlotParts = timeSlot.startTime.split(':').slice(0, 2).join(':');
                const dateStartParts = (foundDate?.startTime || '').split(':').slice(0, 2).join(':');
                return timeSlotParts === dateStartParts;
            });

        isEditingOrRemovingRef.current = true;

        if (foundDate && editShift) {
            let selectedTime;

            // For Open Day shift with only one time slot, pre-select it
            const isOpenDayShift = foundDate.shiftType?.toString() === SHIFT_TYPE_OPEN_DAY;
            if (isOpenDayShift && editShift.timeSlots.length === 1) {
                const [onlyTimeSlot] = editShift.timeSlots;
                selectedTime = [{
                    label: onlyTimeSlot?.label || '',
                    value: onlyTimeSlot?.label || '',
                    startTime: onlyTimeSlot?.startTime || '',
                    endTime: onlyTimeSlot?.endTime || '',
                }];
            } else if (editShift?.name === '24/7' && !foundTimeSlot) {
                selectedTime = [{ label: 'Custom', value: 'Custom', startTime: '', endTime: '' }];
            } else {
                selectedTime = [{
                    label: foundTimeSlot?.label || '',
                    value: foundTimeSlot?.label || '',
                    startTime: foundTimeSlot?.startTime || '',
                    endTime: foundTimeSlot?.endTime || '',
                }];
            }

            // Match the option's display label/value (getShiftLabel = label ||
            // name) so the render-level foundShift resolves and timeSlotOptions
            // populates — otherwise the pre-selected time slot can't display.
            programmaticShiftRef.current = getShiftLabel(editShift);
            setSelectedShift([{ label: getShiftLabel(editShift), value: editShift.value }]);
            setSelectedTimeSlot(selectedTime);
            if (editShift?.name === '24/7' && !foundTimeSlot) {
                setCustomTimeSlotStartTime(`${foundDate.startTime}:00`);
            }
            if (editShift?.name === 'Custom' && !foundTimeSlot && foundDate.startTime) {
                const startTime = `${foundDate.startTime}:00`;
                setCustomShiftStartTime(startTime);
                const shiftDuration = maxShiftHours || 12;
                const [hours, minutes] = foundDate.startTime.split(':').map(Number);
                const startDate = new Date();
                startDate.setHours(hours || 0, minutes || 0, 0);
                const endDate = new Date(startDate.getTime() + shiftDuration * 60 * 60 * 1000);
                const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}:00`;
                setCustomShiftEndTime(endTime);
                // Match the option's display label/value (getShiftLabel = label ||
                // name) so the render-level foundShift resolves and timeSlotOptions
                // populates — otherwise the pre-selected time slot can't display.
                programmaticShiftRef.current = getShiftLabel(editShift);
                setSelectedShift([{ label: getShiftLabel(editShift), value: editShift.value }]);
            }
        }
        if (foundDate) {
            if (foundDate.blockDate) {
                setSelectedDates([{ value: foundDate.blockDate, label: parseUsOrIso(foundDate.blockDate).format('dddd Do MMMM') }]);
            } else {
                setSelectedDates([{ value: foundDate.date, label: parseUsOrIso(foundDate.date).format('dddd Do MMMM') }]);
            }
        }
        setIsSameShift(false);
        handleEditShift(date);
    };
    const allShiftDropdownOptions = shiftOptions.map((option) => ({ label: getShiftLabel(option), value: option.value }));
    // Amending an Emergency Request: only Custom is selectable.
    const shiftDropdownOptions = lockToEmergency
        ? allShiftDropdownOptions.filter((option) => option.value === '30')
        : allShiftDropdownOptions;
    const timeSlotOptions = foundShift?.timeSlots.map((timeSlot) => ({ label: timeSlot.label, value: timeSlot.label, startTime: timeSlot.startTime, endTime: timeSlot.endTime }));
    // Retail 24/7 takes its hours from the store's own open/close times, so
    // there is no start time for the user to pick — only the 7am-7pm grid used
    // elsewhere needs the Custom slot.
    if (selectedShift[0]?.label === '24/7' && !isRetail) {
        timeSlotOptions?.push({ label: 'Custom', value: 'Custom', startTime: '', endTime: '' });
    }
    const unsavedDates = visitDates.filter((date) => !date.saved);
    // Retail 24/7 shows where the shift starts before the user touches it: the
    // opening of the first date it would cover, narrowed to the dates ticked
    // in the dropdown once there are any. Only a time the user picks is kept.
    const retailCoverDefault = isRetail247Pick
        ? retailCoverDefaultStart(
            selectedDates.length > 0
                ? selectedDates.map((date) => String(date.value))
                : visitDates.map((date) => date.date),
            openDaysByDay,
        )
        : null;
    const selectDatesOptions = unsavedDates.map((date) => {
        const formatted = parseUsOrIso(date.date).format('dddd Do MMMM');
        return { value: date.date, label: formatted };
    });

    // Clear stale selections when available dates change
    const selectDatesKey = selectDatesOptions.map((opt) => opt.value).join(',');
    useEffect(() => {
        const validValues = new Set(selectDatesOptions.map((opt) => opt.value));
        setSelectedDates((prev) => prev.filter((d) => validValues.has(d.value)));
    }, [selectDatesKey]);
    const savedDates = visitDates.filter((date) => date.saved);
    const shiftNames = savedDates.map((date) => date.shiftName);
    const uniqueNames = [...new Set(shiftNames)];
    const getSavedDates = () => {
        const savedDateSections = uniqueNames.map((shiftName, index) => (
            <SavedShift
                shiftName={shiftName || ''}
                onEdit={(date) => handleEditTime(date)}
                onRemove={(date) => handleRemoveTime(date)}
                savedDates={savedDates.filter((date) => date.shiftName === shiftName)}
                hideEndTime={isRetailGeneral}
                // eslint-disable-next-line react/no-array-index-key
                key={`${shiftName}-${index}`} />
        ));
        return savedDateSections;
    };

    return (
        <FormSection title="Select Times">
            {savedDates.length > 0 && <div className="saved-dates-container">{getSavedDates()}</div>}
            {isAdmin && onChangeNumberOfOfficers && (
                <>
                    <div className="total-hour officer-count-row">
                        <div className="title-hour">Number of Officers per Shift:</div>
                        <div className="officer-stepper">
                            <button
                                type="button"
                                className="officer-stepper-btn"
                                aria-label="Decrease officers"
                                disabled={numberOfOfficers <= 1}
                                onClick={() => onChangeNumberOfOfficers(Math.max(1, numberOfOfficers - 1))}>
                                −
                            </button>
                            <span className="officer-stepper-value">{numberOfOfficers}</span>
                            <button
                                type="button"
                                className="officer-stepper-btn"
                                aria-label="Increase officers"
                                disabled={numberOfOfficers >= 10}
                                onClick={() => onChangeNumberOfOfficers(Math.min(10, numberOfOfficers + 1))}>
                                +
                            </button>
                        </div>
                    </div>
                    {numberOfOfficers > 1 && (
                        <div className="officer-multi-warning">
                            Warning - You are requesting Multiple Officers
                        </div>
                    )}
                </>
            )}
            {showBilling && (
                <div className="total-hour">
                    <div className="title-hour">Total Hours Billed:</div>
                    <div>{Number((calculateTotalHours * numberOfOfficers).toFixed(2))}</div>
                </div>
            )}
            <div className="select-times-container">
                {(visitDates.length > 1)
                && (!isCustomShift && selectedTimeSlot[0]?.label !== 'Custom')
                && !isRetailGeneral
                && !isRetail247Pick
                && (
                    <div className="checkbox-header">
                        <Checkbox checked={isSameShift} onClick={handleIsSameShiftChange} />
                        <span className="checkbox-label">Request the same shift for all selected dates</span>
                    </div>
                )}
                <div className="content">
                    {isGuardingRequest && !isRetail && !isCustomShift && selectedShift[0]?.value !== '20' && (
                        <WarningBox>The minimum shift is 12 hours. Please select the shift which covers the hours you need to visit the site.</WarningBox>
                    )}
                    {isRetailGeneral ? (
                        <div className="retail-start-time">
                            <div className="heading-wrapper">
                                <h3 className="heading">Shift Start</h3>
                            </div>
                            <TimePicker
                                value={retailGeneralStartTime}
                                onChange={(value) => {
                                    hasPickedRetailStartRef.current = true;
                                    setRetailGeneralStartTime(value);
                                    if (timeSlotError) setTimeSlotError('');
                                }}
                                minuteStep={15}
                                placeholder="Select start time"
                                // The start comes from the store's opening time
                                // for the selected dates, so the picker must not
                                // seed itself with "now" — doing so both showed
                                // the wrong time and counted as a user choice,
                                // which suppressed the opening-time default.
                                autoSelectCurrentTime={false} />
                            {timeSlotError && <span className="error-message">{timeSlotError}</span>}
                        </div>
                    ) : (
                        <>
                            <div className="heading-wrapper">
                                <h3 className="heading">Choose Shift</h3>
                            </div>
                            <div className="shift-dropdown">
                                <Dropdown
                                    options={shiftDropdownOptions}
                                    values={selectedShift}
                                    onSelect={handleShiftChange}
                                    onClose={handleShiftDropdownClose}
                                    placeholder="Please select a shift" />
                            </div>
                            {isCustomShift ? (
                                <div className="custom-shift-container">
                                    {(isAdmin || lockToEmergency) && (
                                        <div className="critical-time-toggle checkbox-wrapper">
                                            <Checkbox checked={isTimeCritical} onClick={handleIsTimeCriticalChange} disabled={lockToEmergency} />
                                            <span className="checkbox-label">Emergency Request</span>
                                        </div>
                                    )}
                                    {!isTimeCritical && (
                                        <WarningBox>
                                            {(() => {
                                                // Mobile response wins over the guarding
                                                // 12-hour minimum because mobile-response
                                                // jobs are capped at maxShiftHours and
                                                // can't be billed up to 12h.
                                                if (maxShiftHours) {
                                                    return `The maximum shift is ${maxShiftHours} hours. You will not be able to select a time slot longer than this.`;
                                                }
                                                if (isGuardingRequest) {
                                                    return 'Please input the hours below that you will be at the site. Please be aware, the minimum shift is 12 hours. If you select a time frame under this, you will still be charged for a 12 hour shift.';
                                                }
                                                return 'The maximum shift is 12 hours, therefore the Shift End time will automatically be set 12 hours from the time you set for the shift to begin.';
                                            })()}
                                        </WarningBox>
                                    )}
                                    <div className="times-wrapper">
                                        <div>
                                            <h5 className="label">Shift Start</h5>
                                            <TimePicker
                                                value={customShiftStartTime}
                                                onChange={handleCustomShiftStartTimeChange}
                                                minuteStep={15}
                                                placeholder="Select time" />
                                        </div>
                                        <div>
                                            <h5 className="label">Shift End</h5>
                                            <TimePicker
                                                value={customShiftEndTime}
                                                onChange={handleCustomShiftEndTimeChange}
                                                disabled={!enabled24_7 && !isGuardingRequest && !maxShiftHours && !isTimeCritical}
                                                minuteStep={15}
                                                placeholder="Select time" />
                                        </div>
                                    </div>
                                </div>
                            ) : selectedShift[0]?.value !== '40' && !isRetailGuarding && (
                                <>
                                    <div className="heading-wrapper">
                                        <h3 className="heading">Time Slots</h3>
                                        <Tooltip title="The minimum time slot available for booking is 8 hours, so please select the window which you wil be visiting in.">
                                            <div className="warning-icon-wrapper">
                                                <WarningIcon />
                                            </div>
                                        </Tooltip>
                                    </div>
                                    <div className="shift-dropdown">
                                        <Dropdown
                                            options={timeSlotOptions || []}
                                            values={selectedTimeSlot}
                                            onSelect={handleTimeSlotChange} />
                                    </div>
                                    {timeSlotError && <span className="error-message">{timeSlotError}</span>}
                                </>
                            )}
                            {isRetail247Pick && (
                                <div className="custom-time-slot-container">
                                    <div className="times-wrapper">
                                        <div>
                                            <h5 className="label">Shift Start</h5>
                                            <TimePicker
                                                value={customTimeSlotStartTime ?? retailCoverDefault}
                                                onChange={handleRetail247StartChange}
                                                autoSelectCurrentTime={false}
                                                minuteStep={15}
                                                placeholder="Select time" />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {selectedTimeSlot[0]?.label === 'Custom' && !isRetailAutoShift && (
                                <div className="custom-time-slot-container">
                                    <WarningBox>
                                        {isGuardingRequest && !isRetail
                                            ? 'You can select the start time of your shift below. After this, all shifts will be back-to-back day and night shifts.'
                                            : 'Select the time you would like the 24/7 shift to start. Each selected date is then covered by two 12 hour shifts from that time.'}
                                    </WarningBox>
                                    <div className="times-wrapper">
                                        <div>
                                            <h5 className="label">Shift Start</h5>
                                            <TimePicker
                                                value={customTimeSlotStartTime}
                                                onChange={handleCustomTimeSlotStartTimeChange}
                                                minuteStep={15}
                                                placeholder="Select time" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    {(!isSameShift || isRetailGeneral || isRetail247Pick) && (
                        <>
                            <h3 className="heading">Select Dates for Chosen Shift</h3>
                            <div className="shift-dropdown">
                                <CheckboxDropdown
                                    options={selectDatesOptions}
                                    values={selectedDates}
                                    onSelect={handleSelectedDatesChange}
                                    onClose={handleDatesDropdownClose}
                                    showSelectAll />
                            </div>
                        </>
                    )}
                    {(!isSameShift || isRetailGeneral || isRetail247Pick) && (
                        <div className="button-wrapper">
                            <Button className="save-button" onClick={handleSaveShiftClick}>Save Shift</Button>
                            {/* <Button className="add-button">+ Add Another Shift</Button> */}
                        </div>
                    )}
                </div>
            </div>
        </FormSection>
    );
};

ShiftSelector.defaultProps = {
    totalHours: 0,
    openDays: undefined,
    openDaysByDay: undefined,
    bankHolidays: undefined,
    onChangetotalHours: () => { /* no-op */ },
    showBilling: true,
    maxShiftHours: 0,
};

export default ShiftSelector;
